import CampaignCardLink from '@components/campaign-card-link/campaign-card-link.component'
import CampaignCard from '@components/campaign-card/campaign-card.component'
import CampaignTinyCard from '@components/campaign-tiny-card/campaign-tiny-card.component'
import { Button, Campaign, ObjectHelper, UserInCampaign } from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import React, { LegacyRef, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CampaignListHeader from '../campaign-list-header/campaign-list-header.component'
import './section-campaigns.component.scoped.scss'
import './section-campaigns.component.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  type: string
  route?: string
  icon?: any
  img?: any
  loading?: boolean
  campaignList: Array<{ campaign: Campaign; additionalInformation: UserInCampaign }>
  smallCard?: boolean
  bottomLine?: boolean
  badge?: boolean
}

export default ({
  type = '',
  className = '',
  route = '',
  icon = null,
  img = null,
  loading = false,
  campaignList = null,
  smallCard = false,
  bottomLine = false,
  badge = false,
}: Props) => {
  const timestamp = `random-id-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`
  const [t] = useTranslation('homeCampaign')
  const objectHelper = useMemo(() => new ObjectHelper(), [])
  const sliderCampaignsRef: LegacyRef<HTMLDivElement> = React.createRef()
  const ulRef: LegacyRef<HTMLUListElement> = React.createRef()
  const [innerWidth, setInnerWidth] = useState(window.innerWidth)
  const [state, setState] = useState({
    activeIndex: 0,
    indexWidth: 0,
    ulWidth: 0,
    sliderCampaignsWidth: 0,
    maxSlider: 0,
    campaignList: [] as Array<{ campaign: Campaign; additionalInformation: UserInCampaign }>,
  })

  useLayoutEffect(() => {
    const resize = () => {
      setInnerWidth(window.innerWidth)
    }

    resize()
    window.addEventListener('resize', resize)
    return () => {
      window.removeEventListener('resize', resize)
    }
  })

  const isTouchDevice = () => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0
  }

  useEffect(() => {
    if (
      ulRef?.current?.offsetWidth &&
      sliderCampaignsRef?.current?.offsetWidth &&
      (!objectHelper.arraysMatch(state.campaignList, campaignList) ||
        state.ulWidth !== ulRef?.current?.offsetWidth ||
        state.sliderCampaignsWidth !== sliderCampaignsRef?.current?.offsetWidth)
    ) {
      const listLength = campaignList?.length > 5 ? 5 : campaignList?.length
      const ulWidth = ulRef?.current?.offsetWidth
      const sliderCampaignsWidth = sliderCampaignsRef?.current?.offsetWidth
      const indexWidth = (ulWidth + 21) / listLength
      const aux = Math.trunc((sliderCampaignsWidth + (campaignList?.length > 5 && !smallCard ? 135 : 0)) / indexWidth)
      const maxSlider = aux >= listLength ? 0 : listLength - aux

      setState({
        ...state,
        activeIndex: 0,
        indexWidth: indexWidth,
        ulWidth: ulWidth,
        sliderCampaignsWidth: sliderCampaignsWidth,
        maxSlider: maxSlider,
        campaignList: campaignList,
      })
    }
  }, [campaignList, state, ulRef, sliderCampaignsRef, objectHelper, smallCard, setState, innerWidth])

  const updateIndex = useCallback(
    (newIndex) => {
      if (newIndex?.deltaY) {
        newIndex.preventDefault()
        newIndex = newIndex?.deltaY
      }
      if (newIndex < 0 && state.activeIndex > 0) {
        state.activeIndex--
      } else if (newIndex > 0 && state.activeIndex < state.maxSlider) {
        state.activeIndex++
      }
      setState({ ...state, activeIndex: state.activeIndex })
    },
    [state, setState]
  )

  useEffect(() => {
    if (sliderCampaignsRef?.current && state.maxSlider === 0) {
      sliderCampaignsRef.current.onwheel = null
    } else if (!isTouchDevice() && sliderCampaignsRef?.current && state.activeIndex < state.maxSlider) {
      sliderCampaignsRef.current.onwheel = updateIndex
    }
  }, [sliderCampaignsRef, updateIndex, state.activeIndex, state.maxSlider])

  const controllerButtons = (
    <div className="campaign-list-buttons">
      <Button
        className={`controller-button left ${!campaignList?.length || state.activeIndex === 0 ? 'display-none' : ''}`}
        onClick={() => updateIndex(-1)}
        color="var(--background_secondary)"
        borderColor="var(--border_color)"
        rounded
        inverted
      >
        <i className="fa-solid fa-chevron-left" style={{ color: 'var(--text_color)' }}></i>
      </Button>
      <Button
        className={`controller-button right ${!campaignList?.length || state.activeIndex === state.maxSlider ? 'display-none' : ''}`}
        onClick={() => updateIndex(1)}
        color="var(--background_secondary)"
        borderColor="var(--border_color)"
        rounded
        inverted
      >
        <i className="fa-solid fa-chevron-right" style={{ color: 'var(--text_color)' }}></i>
      </Button>
    </div>
  )

  return (
    <section className={`section-campaigns ${className}`}>
      <CampaignListHeader
        title={t(`${type}.title`)}
        subTitle={t(`${type}.subtitle`)}
        route={!loading && campaignList?.length > 5 ? route : null}
        icon={
          img ? (
            <img src={img} title="Research" alt="" />
          ) : icon ? (
            <i className={`${icon}`}></i>
          ) : badge ? (
            <span
              className="display-flex align-items-center justify-content-center badge background-blue-dark white rounded"
              style={{ textAlign: 'center', fontSize: '1rem' }}
            >
              {campaignList?.length}
            </span>
          ) : null
        }
      />
      {loading ? (
        <div className="text-center py-5" style={{ width: '100%' }}>
          <SqLoader size="small" />
        </div>
      ) : (
        <div className="slider-campaigns" ref={sliderCampaignsRef}>
          <div className="campaign-list" style={{ width: '100%' }}>
            {campaignList?.length ? (
              <ul
                style={{
                  transform: `translateX(${
                    state.maxSlider > 0 && state.activeIndex === state.maxSlider
                      ? state.sliderCampaignsWidth - (state.ulWidth + 21) + (campaignList?.length > 5 && !smallCard ? 135 : 0)
                      : state.activeIndex * -state.indexWidth
                  }px)`,
                  transitionDuration: '500ms',
                  userSelect: 'none',
                }}
                ref={ulRef}
              >
                {campaignList?.map((campaignObject, index) =>
                  smallCard && index < 5 ? (
                    <CampaignTinyCard
                      campaign={campaignObject?.campaign}
                      users={campaignObject?.additionalInformation}
                      key={`${campaignObject?.campaign?._id}-${index}`}
                      type={type}
                    />
                  ) : index < 5 ? (
                    <CampaignCard
                      campaign={campaignObject?.campaign}
                      users={campaignObject?.additionalInformation}
                      key={`${campaignObject?.campaign?._id}-${index}`}
                      type={type}
                    />
                  ) : (
                    <CampaignCardLink orientation={smallCard ? 'horizontal' : 'vertical'} link={route} key={`${timestamp}-${index}`}>
                      <div dangerouslySetInnerHTML={{ __html: t(`${type}.seeMoreMessage`) }} />
                    </CampaignCardLink>
                  )
                )}
              </ul>
            ) : (
              <div className="without-campaign-warning">
                <h5 className="m-0" title={t(`${type}.warning`)}>
                  {t(`${type}.warning`)}
                </h5>
              </div>
            )}
          </div>
          {controllerButtons}
        </div>
      )}
      {bottomLine ? <hr /> : null}
    </section>
  )
}
